import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, KeyRound, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function SetPassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const longEnough = pw.length >= 8;
  const matches = pw.length > 0 && pw === confirm;
  const canSubmit = longEnough && matches && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!longEnough) { setError("Password must be at least 8 characters."); return; }
    if (!matches) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      await refresh();
      toast.success("Password updated");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const Req = ({ ok, children }) => (
    <li className={`flex items-center gap-1.5 ${ok ? "text-emerald-600" : "text-slate-400"}`}>
      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {children}
    </li>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" data-testid="set-password-screen">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <span className="font-display text-xl font-black text-slate-900">Watch<span className="text-blue-600">Relay</span></span>
        </div>

        <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Create your new password</h1>
        <p className="mt-1 text-sm text-slate-500">
          Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. For security, set your own password before continuing to WatchRelay.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="new-pw" className="text-xs font-bold uppercase tracking-wider text-slate-500">New password</Label>
            <Input id="new-pw" data-testid="new-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5" autoFocus />
          </div>
          <div>
            <Label htmlFor="confirm-pw" className="text-xs font-bold uppercase tracking-wider text-slate-500">Confirm new password</Label>
            <Input id="confirm-pw" data-testid="confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
          </div>

          <ul className="space-y-1 text-xs">
            <Req ok={longEnough}>At least 8 characters</Req>
            <Req ok={matches}>Passwords match</Req>
          </ul>

          {error && <p data-testid="set-password-error" className="text-sm font-medium text-red-600">{error}</p>}

          <Button type="submit" data-testid="set-password-submit" disabled={!canSubmit} className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
            <KeyRound className="h-4 w-4" /> {busy ? "Saving…" : "Set password & continue"}
          </Button>
        </form>

        <button
          data-testid="set-password-signout"
          onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
          className="mt-4 w-full text-center text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
